import clsx from 'clsx';
import * as React from 'react';
import { mergeProps } from 'react-aria';

import { Icon } from '@gitbook/spine-icons';

import type { AlertKind } from '../Alert';
import { Button, useButton } from '../Button';
import type { ButtonProps } from '../Button/Button.types';
import { LoadingIndicator } from '../Loading/LoadingIndicator';
import { Tooltip } from '../Tooltip';
import { type IconDefinition, renderIcon } from '../util';

export type StatusAction = Required<Pick<ButtonProps, 'label' | 'onPress'>>;

export type StatusSize = 'small' | 'medium';

export type StatusProps = Omit<React.ComponentPropsWithoutRef<'div'>, 'children' | 'label'> & {
    /** Semantic tone used for the indicator and overall status meaning. */
    kind: AlertKind;
    /** Primary visible text for the status pill. */
    label: React.ReactNode;
    /** Optional secondary content shown inline or in a tooltip. */
    detail?: React.ReactNode;
    /** Render `detail` in a tooltip on hover/focus instead of inline in the pill. */
    detailTooltip?: boolean;
    /** Optional leading icon; when present, the indicator overlays its top-right corner. */
    icon?: IconDefinition;
    /** Press handler for the whole pill. */
    onPress?: () => void;
    /** Replaces the indicator dot with a loading spinner when `true`. */
    loading?: boolean;
    /** Hide the semantic status dot while preserving the rest of the status layout. */
    showStatusDot?: boolean;
    /** Optional trailing action: renders a primary, fully rounded `small` {@link Button} */
    action?: StatusAction;
    /**
     * Pill size.
     * - `medium` (default): full-size pill that inherits the surrounding text and icon size.
     * - `small`: ~80% scale — tighter padding, 10px icon, smaller dot
     * @default 'medium'
     */
    size?: StatusSize;
};

/**
 * Pill-shaped status indicator with an optional semantic dot, or a smaller indicator overlaid on an icon when provided. Differentiates from a Badge and Alert by:
 * - small affordance but provides a lot of space for communicating status to readers
 * - can surface status semantically through an indicator, loading state, or just the label/detail pair
 * - more room for conveying status info with the `label` and the optional `detail`
 * - allows for a trailing action button or an onPress on the status pill itself
 */
export function Status({ ref, ...props }: StatusProps & { ref?: React.Ref<HTMLDivElement> }) {
    const {
        kind,
        label,
        detail,
        detailTooltip = false,
        icon,
        className,
        onPress,
        loading,
        showStatusDot = true,
        action,
        size = 'medium',
        ...rest
    } = props;
    const { buttonProps, isFocusVisible } = useButton({ elementType: 'div', onPress });
    const isInteractive = onPress != null;
    const isSmall = size === 'small';

    // Size-conditional dimensions.
    const iconPixelSize = isSmall ? 10 : 16;
    const overlayDotClass = isSmall ? 'size-1.5' : 'size-2';
    const standaloneDotClass = isSmall ? 'size-2 shrink-0 border' : 'size-2.5 shrink-0 border-2';
    const loadingPixelSize = isSmall ? 8 : 'inherit';
    const standaloneLoadingPixelSize = isSmall ? 10 : 'inherit';
    const inlineDetail = detailTooltip ? undefined : detail;
    const tooltipDetail = detailTooltip ? detail : undefined;
    const textSizeClass = isSmall ? 'text-ui-small' : 'text-[length:inherit] leading-[inherit]';

    const statusElement = (
        <div
            ref={ref}
            aria-busy={loading || undefined}
            {...(isInteractive ? mergeProps(rest, buttonProps) : rest)}
            className={clsx(
                'inline-flex w-fit max-w-full shrink-0 items-center rounded-full border border-control bg-base',
                isSmall ? 'gap-1' : 'gap-2 py-1',
                isSmall ? (action ? 'pr-0.5 pl-1.5' : 'px-1.5') : action ? 'pr-1 pl-2' : 'px-2',
                isInteractive &&
                    clsx(
                        'cursor-pointer outline-hidden transition-colors hover:bg-neutral-ui',
                        // Suppress pill hover fill while the pointer is on the trailing action (still within the outer :hover).
                        action && '[&:has([data-status-action]:hover)]:hover:bg-base',
                        isFocusVisible && 'ring-2 ring-primary ring-offset-2'
                    ),
                className
            )}
        >
            {/* Leading: icon + dot/spinner overlay, or standalone dot/spinner. */}
            {icon ? (
                <span className="relative inline-flex shrink-0">
                    {renderIcon(icon, { size: iconPixelSize, className: 'shrink-0' })}
                    {loading ? (
                        <span
                            aria-hidden
                            className="pointer-events-none absolute -top-0.5 -right-0.5 flex items-center justify-center rounded-[100px] bg-base shadow-[0_0_0_1px_var(--neutral-1),inset_0_0_0_2px_var(--neutral-1)]"
                        >
                            <LoadingIndicator
                                className="text-inherit"
                                size={loadingPixelSize}
                                spinnerIcon={Icon.Status}
                            />
                        </span>
                    ) : showStatusDot ? (
                        <span
                            aria-hidden
                            className={clsx(
                                'absolute -top-0.5 -right-0.5 box-border rounded-full border border-solid',
                                overlayDotClass,
                                STATUS_DOT_BG[kind],
                                STATUS_DOT_BORDER[kind]
                            )}
                        />
                    ) : null}
                </span>
            ) : loading ? (
                <span aria-hidden className="inline-flex shrink-0 items-center justify-center">
                    <LoadingIndicator
                        className="text-inherit"
                        size={standaloneLoadingPixelSize}
                        spinnerIcon={Icon.Status}
                    />
                </span>
            ) : showStatusDot ? (
                <span
                    aria-hidden
                    className={clsx(
                        'box-border rounded-full border-solid',
                        standaloneDotClass,
                        STATUS_DOT_BG[kind],
                        STATUS_DOT_BORDER[kind]
                    )}
                />
            ) : null}

            <div
                className={clsx(
                    'flex min-w-0 items-center',
                    action && 'min-h-0 flex-1 overflow-hidden'
                )}
            >
                {inlineDetail ? (
                    <div
                        className={clsx(
                            'flex min-w-0 items-center gap-x-3',
                            action && 'min-h-0 flex-1 overflow-hidden'
                        )}
                    >
                        <StatusLabel className={clsx('font-[450]', textSizeClass)}>
                            {label}
                        </StatusLabel>
                        <span
                            className={clsx(
                                'text-nowrap text-neutral-low',
                                textSizeClass,
                                action ? 'min-w-0 flex-1 basis-0 truncate' : 'shrink-0'
                            )}
                        >
                            {inlineDetail}
                        </span>
                    </div>
                ) : (
                    <StatusLabel className={textSizeClass}>{label}</StatusLabel>
                )}
            </div>

            {action ? (
                // Stop pointerdown so the outer pressable shell doesn't fire when the trailing button is clicked.
                <span
                    className="flex shrink-0 items-center"
                    data-status-action
                    onPointerDown={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        kind="primary"
                        label={action.label}
                        rounded
                        size="small"
                        onPress={action.onPress}
                    />
                </span>
            ) : null}
        </div>
    );

    return tooltipDetail ? (
        <Tooltip tooltip={tooltipDetail}>{statusElement}</Tooltip>
    ) : (
        statusElement
    );
}

/** Primary label span used inside {@link Status} */
function StatusLabel(props: { children: React.ReactNode; className?: string }) {
    return <span className={clsx('shrink-0 text-nowrap', props.className)}>{props.children}</span>;
}

/** Solid alert dot fill per {@link AlertKind} (semantic solids). */
const STATUS_DOT_BG: Record<AlertKind, string> = {
    default: 'bg-[var(--neutral-3)]',
    info: 'bg-[var(--info-solid)]',
    danger: 'bg-[var(--danger-solid)]',
    success: 'bg-[#4dde98]', // the green from our shades.css is too dark
    merge: 'bg-[var(--merge-solid)]',
    upgrade: 'bg-[var(--upgrade-solid)]',
    warning: 'bg-[var(--warning-solid)]',
};

/**
 * Border color for the alert dot (step-4 / `*-ui-bg-hover` per scale), e.g. info matches `2px solid var(--blue-4)` via `--info-ui-bg-hover`.
 * Standalone dots use `border-2`; the overlay dot on an icon uses `border` (1px) with the same token.
 */
const STATUS_DOT_BORDER: Record<AlertKind, string> = {
    default: 'border-[var(--neutral-1)]',
    info: 'border-[var(--info-ui-bg-hover)]',
    danger: 'border-[var(--danger-ui-bg-hover)]',
    success: 'border-[var(--success-ui-bg-hover)]',
    merge: 'border-[var(--merge-ui-bg-hover)]',
    upgrade: 'border-[var(--upgrade-ui-bg-hover)]',
    warning: 'border-[var(--warning-ui-bg-hover)]',
};
